import { CookieConsentPreferences } from "@utils/cookieConsent"
import colors from "@constants/colors"
import MarkdownIt from "markdown-it"
import React from "react"
import { up } from "styled-breakpoints"
import styled, { css } from "styled-components"

const Markdown = new MarkdownIt()

const resetButton = css`
  border: 0;
  background: transparent;
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.black};
    outline-offset: 3px;
  }
`

const Container = styled.section<{ showBanner: boolean }>`
  display: ${({ showBanner }) => (showBanner ? "block" : "none")};
  box-sizing: border-box;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 20px;
  color: ${colors.obsidian};

  ${up("laptop")} {
    padding: 24px 5vw;
  }
`

const Summary = styled.div`
  ${up("laptop")} {
    align-items: center;
    justify-content: space-between;
    display: flex;
    gap: 48px;
  }
`

const Copy = styled.div`
  max-width: 820px;
`

const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 16px;
  line-height: 1.3;
`

const Body = styled.div`
  color: ${colors.slateDark};
  font-size: 13px;
  line-height: 1.4;

  p {
    margin: 0;
  }

  a {
    color: inherit;
    text-decoration: underline;
  }
`

const Actions = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 18px;

  ${up("tablet")} {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
  }

  ${up("laptop")} {
    flex: 0 0 470px;
    margin-top: 0;
  }
`

const Button = styled.button<{ primary?: boolean }>`
  ${resetButton}
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid ${colors.obsidian};
  border-radius: 3px;
  background: ${({ primary }) => (primary ? colors.primary : colors.snow)};
  color: ${({ primary }) => (primary ? colors.snow : colors.obsidian)};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
`

const PreferencesHeader = styled.div`
  max-width: 680px;
  margin-bottom: 12px;
`

const PreferenceList = styled.div`
  display: grid;
  gap: 6px;
  margin: 12px 0 16px;
`

const Preference = styled.label<{ $disabled?: boolean }>`
  align-items: flex-start;
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${colors.divider};
  border-radius: 4px;
  background: ${({ $disabled }) =>
    $disabled ? colors.lightestGray : colors.snow};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};

  input {
    width: 18px;
    height: 18px;
    margin: 1px 0 0;
    accent-color: ${colors.primary};
  }
`

const PreferenceName = styled.strong`
  display: block;
  margin-bottom: 4px;
  font-size: 15px;
`

const PreferenceDescription = styled.span`
  display: block;
  color: ${colors.slateDark};
  font-size: 13px;
  line-height: 1.35;
`

const PreferenceActions = styled(Actions)`
  max-width: 680px;
  margin: 16px auto 0;

  ${up("laptop")} {
    width: 680px;
    max-width: 100%;
    margin: 16px auto 0;
  }
`

const BackButton = styled.button`
  ${resetButton}
  margin-bottom: 14px;
  padding: 0;
  color: ${colors.slateDark};
  text-decoration: underline;
`

export interface CookieBannerProps {
  showBanner: boolean
  header: string
  body: string
  acceptText: string
  declineText: string
  initialPreferences?: CookieConsentPreferences
  startInPreferences?: boolean
  globalPrivacyControlEnabled?: boolean
  onAccept: () => void
  onDecline: () => void
  onSave?: (preferences: CookieConsentPreferences) => void
}

const CookieBanner: React.FC<CookieBannerProps> = ({
  showBanner,
  header,
  body,
  acceptText,
  declineText,
  initialPreferences = { analytics: false, marketing: false },
  startInPreferences = false,
  globalPrivacyControlEnabled = false,
  onAccept,
  onDecline,
  onSave,
}) => {
  const [preferencesOpen, setPreferencesOpen] =
    React.useState(startInPreferences)
  const [preferences, setPreferences] = React.useState(initialPreferences)

  React.useEffect(() => {
    if (!showBanner) return
    setPreferencesOpen(startInPreferences)
    setPreferences(initialPreferences)
  }, [
    showBanner,
    startInPreferences,
    initialPreferences.analytics,
    initialPreferences.marketing,
  ])

  if (preferencesOpen) {
    return (
      <Container
        showBanner={showBanner}
        role="dialog"
        aria-labelledby="cookie-preferences-title"
      >
        {!startInPreferences && (
          <BackButton type="button" onClick={() => setPreferencesOpen(false)}>
            Back
          </BackButton>
        )}
        <PreferencesHeader>
          <Title id="cookie-preferences-title">You control your data</Title>
          <Body>
            Learn more about the cookies we use, and choose which cookies to
            allow. You can change these choices at any time.
            {globalPrivacyControlEnabled && (
              <p>
                Global Privacy Control is enabled. We will honor this signal by
                keeping marketing and targeted-advertising cookies off.
              </p>
            )}
          </Body>
        </PreferencesHeader>

        <PreferenceList>
          <Preference $disabled>
            <input type="checkbox" checked disabled />
            <span>
              <PreferenceName>Essential</PreferenceName>
              <PreferenceDescription>
                Required for core site functions such as security, navigation,
                and remembering your privacy choices. These cannot be disabled.
              </PreferenceDescription>
            </span>
          </Preference>

          <Preference>
            <input
              type="checkbox"
              checked={preferences.analytics}
              onChange={(event) =>
                setPreferences({
                  ...preferences,
                  analytics: event.target.checked,
                })
              }
            />
            <span>
              <PreferenceName>Analytics</PreferenceName>
              <PreferenceDescription>
                Helps us understand how visitors interact with the site and
                identify areas to improve.
              </PreferenceDescription>
            </span>
          </Preference>

          <Preference>
            <input
              type="checkbox"
              checked={preferences.marketing}
              disabled={globalPrivacyControlEnabled}
              onChange={(event) =>
                setPreferences({
                  ...preferences,
                  marketing: event.target.checked,
                })
              }
            />
            <span>
              <PreferenceName>Marketing</PreferenceName>
              <PreferenceDescription>
                Allows advertising partners to measure campaigns and show more
                relevant marketing on other websites.
                {globalPrivacyControlEnabled &&
                  " Disabled because your browser is sending a Global Privacy Control signal."}
              </PreferenceDescription>
            </span>
          </Preference>
        </PreferenceList>

        <PreferenceActions>
          <Button type="button" onClick={onAccept}>
            {globalPrivacyControlEnabled ? "Accept analytics" : "Accept all"}
          </Button>
          <Button type="button" onClick={onDecline}>
            Decline all
          </Button>
          <Button
            type="button"
            primary
            onClick={() => onSave && onSave(preferences)}
          >
            Save my choices
          </Button>
        </PreferenceActions>
      </Container>
    )
  }

  return (
    <Container
      showBanner={showBanner}
      role="dialog"
      aria-labelledby="cookie-banner-title"
    >
      <Summary>
        <Copy>
          <Title id="cookie-banner-title">{header}</Title>
          <Body dangerouslySetInnerHTML={{ __html: Markdown.render(body) }} />
        </Copy>
        <Actions>
          <Button type="button" onClick={onDecline}>
            {declineText}
          </Button>
          <Button type="button" onClick={() => setPreferencesOpen(true)}>
            Manage preferences
          </Button>
          <Button type="button" primary onClick={onAccept}>
            {acceptText}
          </Button>
        </Actions>
      </Summary>
    </Container>
  )
}

export default CookieBanner
